import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { supabase } from '../../lib/supabase';
import { adminUserService } from '../../services/adminUserService';
import Navigation from '../../components/Navigation';
import AdminSidebar from '../../components/AdminSidebar';
import { Users, Search, RefreshCw, Upload, CheckCircle2, XCircle, Clock, AlertCircle, ChevronDown, Link2, FileUp, Check, Minus, Mail, Lock, Ban, X, Send, RotateCcw } from 'lucide-react';

// ─── helpers ────────────────────────────────────────────────────────────────
const TIME_FILTERS = [
  { label: 'Svi', value: 'all' },
  { label: 'Poslednjih 24h', value: '24h' },
  { label: 'Poslednjih 7 dana', value: '7d' },
  { label: 'Poslednjih godinu dana', value: '1y' },
];

function getDateFrom(filter) {
  const now = new Date();
  if (filter === '24h') { now?.setHours(now?.getHours() - 24); return now?.toISOString(); }
  if (filter === '7d') { now?.setDate(now?.getDate() - 7); return now?.toISOString(); }
  if (filter === '1y') { now?.setFullYear(now?.getFullYear() - 1); return now?.toISOString(); }
  return null;
}

// Normalize header key: lowercase, trim, remove special chars
function normalizeKey(str) {
  return (str || '')?.toString()?.toLowerCase()?.trim()?.replace(/[^a-z0-9]/g, '');
}

// Map normalized header to our field names
function mapHeader(raw) {
  const k = normalizeKey(raw);
  if (k?.includes('fullname') || k?.includes('name') || k?.includes('ime') || k === 'full') return 'fullName';
  if (k?.includes('email') || k?.includes('mail')) return 'email';
  if (k?.includes('role') || k?.includes('uloga')) return 'role';
  if (k?.includes('status')) return 'status';
  if (k?.includes('state') || k?.includes('stanje')) return 'state';
  if (k?.includes('standing') || k?.includes('ocena')) return 'standing';
  if (k?.includes('maintenance') || k?.includes('odrzavanje') || k?.includes('datum')) return 'maintenance';
  if (k?.includes('details') || k?.includes('detalji') || k?.includes('view')) return 'details';
  return null;
}

function parseExcelUsers(arrayBuffer) {
  const workbook = XLSX?.read(arrayBuffer, { type: 'array' });
  const sheetName = workbook?.SheetNames?.[0];
  if (!sheetName) return [];
  const sheet = workbook?.Sheets?.[sheetName];
  const jsonRows = XLSX?.utils?.sheet_to_json(sheet, { header: 1, defval: '' });
  if (!jsonRows || jsonRows?.length < 2) return [];
  const rawHeaders = jsonRows?.[0];
  const fieldMap = rawHeaders?.map(h => mapHeader(h));
  const emailIdx = fieldMap?.indexOf('email');
  if (emailIdx === -1) return [];
  return jsonRows?.slice(1)?.map(row => {
    const obj = {};
    fieldMap?.forEach((field, i) => { if (field) obj[field] = (row?.[i] || '')?.toString()?.trim(); });
    return obj;
  })?.filter(u => u?.email && u?.email?.includes('@'));
}

const ROLE_LABELS = { user: 'Korisnik', verified_user: 'Verifikovan', moderator: 'Moderator', admin: 'Admin', super_admin: 'Super Admin' };
const ROLE_COLORS = { user: 'bg-gray-100 text-gray-700', verified_user: 'bg-blue-100 text-blue-700', moderator: 'bg-purple-100 text-purple-700', admin: 'bg-amber-100 text-amber-700', super_admin: 'bg-red-100 text-red-700' };

// ─── Email Modal ─────────────────────────────────────────────────────────────
// Helper: open Gmail compose window
function openGmailCompose(to, subject, body) {
  const url = `https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(to)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  window.open(url, '_blank');
}

function SendEmailModal({ users, onClose, onSent }) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [subject, setSubject] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    adminUserService?.getEmailTemplates()?.then(t => setTemplates(t || []))?.catch(() => {});
  }, []);

  const handleTemplateChange = (id) => {
    setSelectedTemplateId(id);
    const tpl = templates?.find(t => t?.id === id);
    if (tpl) {
      setSubject(tpl?.subject || '');
      setCustomMessage(tpl?.body || tpl?.bodyTemplate || '');
    }
  };

  const handleSend = () => {
    if (!subject?.trim()) { setError('Unesite naslov emaila'); return; }
    if (!customMessage?.trim()) { setError('Unesite poruku'); return; }
    setError('');
    // Open Gmail compose for each recipient (or comma-separated for multiple)
    const toList = users?.map(u => u?.email)?.join(',');
    const bodyText = customMessage;
    openGmailCompose(toList, subject, bodyText);
    onSent?.();
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-slate-800">Pošalji poruku</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="bg-blue-50 rounded-xl px-4 py-2.5 text-sm text-blue-700">
            Primaoci: <span className="font-semibold">{users?.length} korisnik{users?.length === 1 ? '' : users?.length < 5 ? 'a' : 'a'}</span>
            {users?.length <= 3 && (
              <span className="text-blue-500 ml-1">({users?.map(u => u?.email)?.join(', ')})</span>
            )}
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-sm text-amber-700 flex items-start gap-2">
            <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>Email će biti otvoren u Gmail-u. Prijavite se kao <strong>admin@valuebook.app</strong> i pošaljite ručno.</span>
          </div>

          {templates?.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Predložak emaila (opciono)</label>
              <select
                value={selectedTemplateId}
                onChange={e => handleTemplateChange(e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">— Bez predloška —</option>
                {templates?.map(t => (
                  <option key={t?.id} value={t?.id}>{t?.name} ({t?.category})</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Naslov emaila *</label>
            <input
              type="text"
              value={subject}
              onChange={e => setSubject(e?.target?.value)}
              placeholder="Unesite naslov..."
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Poruka *</label>
            <textarea
              value={customMessage}
              onChange={e => setCustomMessage(e?.target?.value)}
              rows={5}
              placeholder="Unesite poruku... Možete koristiti {{full_name}} za ime korisnika."
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />{error}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors">Otkaži</button>
          <button
            onClick={handleSend}
            className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition-colors"
          >
            <Mail className="w-4 h-4" />
            Otvori u Gmail-u
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Suspend Modal ────────────────────────────────────────────────────────────
function SuspendModal({ user, adminId, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    setLoading(true);
    setError('');
    try {
      await adminUserService?.suspendUser(user?.id, reason, adminId);
      if (sendEmail && user?.email) {
        const bodyText = `Poštovani/a ${user?.full_name || user?.email},\n\nVaš nalog na ValueBook Marketplace platformi je privremeno suspendovan.${reason ? `\n\nRazlog: ${reason}` : ''}\n\nUkoliko smatrate da je ovo greška, kontaktirajte administratora.\n\nValueBook Marketplace`;
        openGmailCompose(
          user?.email,
          'Vaš nalog je suspendovan — ValueBook Marketplace',
          bodyText
        );
      }
      onDone?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Greška');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-amber-500" />
            <h3 className="font-semibold text-slate-800">Blokiraj korisnika</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="bg-amber-50 rounded-xl px-4 py-3">
            <p className="text-sm text-amber-800">
              Korisnik <strong>{user?.full_name || user?.email}</strong> biće suspendovan i neće moći da pristupi platformi.
              Sve aktivnosti ostaju sačuvane.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Razlog suspenzije (opciono)</label>
            <textarea
              value={reason}
              onChange={e => setReason(e?.target?.value)}
              rows={3}
              placeholder="Unesite razlog..."
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={e => setSendEmail(e?.target?.checked)}
              className="w-4 h-4 rounded border-slate-300 text-amber-500 focus:ring-amber-500"
            />
            <span className="text-sm text-slate-700">Otvori Gmail sa obaveštenjem korisniku</span>
          </label>
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
              <AlertCircle className="w-4 h-4" />{error}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors">Otkaži</button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="flex items-center gap-2 px-5 py-2 bg-amber-500 text-white text-sm font-medium rounded-xl hover:bg-amber-600 disabled:opacity-50 transition-colors"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            {loading ? 'Blokiranje...' : 'Blokiraj'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Ban + Archive Modal ──────────────────────────────────────────────────────
function BanArchiveModal({ user, adminId, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    setLoading(true);
    setError('');
    try {
      if (sendEmail && user?.email) {
        const bodyText = `Poštovani/a ${user?.full_name || user?.email},\n\nVaš nalog na ValueBook Marketplace platformi je trajno banovan, a sve vaše aktivnosti su arhivirane.${reason ? `\n\nRazlog: ${reason}` : ''}\n\nUkoliko smatrate da je ovo greška, kontaktirajte administratora.\n\nValueBook Marketplace`;
        openGmailCompose(
          user?.email,
          'Vaš nalog je banovan — ValueBook Marketplace',
          bodyText
        );
      }
      await adminUserService?.banAndArchiveUser(user?.id, reason, adminId);
      onDone?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Greška');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Ban className="w-5 h-5 text-red-500" />
            <h3 className="font-semibold text-slate-800">Banovaj + arhiviraj</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="bg-red-50 rounded-xl px-4 py-3">
            <p className="text-sm text-red-800">
              <strong>Ovo je trajna akcija.</strong> Korisnik <strong>{user?.full_name || user?.email}</strong> biće trajno banovan.
              Sve aktivnosti (oglasi, pregovori, ocene) biće arhivirane i nevidljive ostalim korisnicima.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Razlog bana *</label>
            <textarea
              value={reason}
              onChange={e => setReason(e?.target?.value)}
              rows={3}
              placeholder="Unesite razlog bana..."
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={e => setSendEmail(e?.target?.checked)}
              className="w-4 h-4 rounded border-slate-300 text-red-500 focus:ring-red-500"
            />
            <span className="text-sm text-slate-700">Otvori Gmail sa obaveštenjem korisniku</span>
          </label>
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
              <AlertCircle className="w-4 h-4" />{error}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors">Otkaži</button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="flex items-center gap-2 px-5 py-2 bg-red-600 text-white text-sm font-medium rounded-xl hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
            {loading ? 'Banovanjem...' : 'Banovaj + arhiviraj'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── main component ──────────────────────────────────────────────────────────
export default function AdminUserManagementDashboard() {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentAdminId, setCurrentAdminId] = useState(null);

  // ── registered users state ──
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [timeFilter, setTimeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('all');
  const [totalCount, setTotalCount] = useState(0);

  // ── selection state ──
  const [selectedIds, setSelectedIds] = useState(new Set());

  // ── modal state ──
  const [emailModal, setEmailModal] = useState(null); // null | { users: [] }
  const [suspendModal, setSuspendModal] = useState(null); // null | user
  const [banModal, setBanModal] = useState(null); // null | user

  // ── valuebook import state ──
  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [importError, setImportError] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importDone, setImportDone] = useState(false);
  const [syncGroups, setSyncGroups] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);

  // ── get current admin ──
  useEffect(() => {
    supabase?.auth?.getUser()?.then(({ data }) => {
      if (data?.user?.id) setCurrentAdminId(data?.user?.id);
    });
  }, []);

  // ─── load registered users ───────────────────────────────────────────────
  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    setUsersError('');
    try {
      let query = supabase
        ?.from('user_profiles')
        ?.select('id, email, full_name, role, is_verified, created_at, valuebook_link, company_id, is_banned, is_suspended, banned_reason, banned_at', { count: 'exact' })
        ?.order('created_at', { ascending: false });

      const dateFrom = getDateFrom(timeFilter);
      if (dateFrom) query = query?.gte('created_at', dateFrom);

      if (searchQuery?.trim() && searchQuery !== 'all') {
        const q = searchQuery?.trim();
        query = query?.or(`email.ilike.%${q}%,full_name.ilike.%${q}%`);
      }

      const { data, error, count } = await query;
      if (error) throw error;
      setUsers(data || []);
      setTotalCount(count || 0);
      setSelectedIds(new Set());
    } catch (err) {
      setUsersError(err?.message || 'Greška pri učitavanju korisnika');
    } finally {
      setUsersLoading(false);
    }
  }, [timeFilter, searchQuery]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  // ── selection helpers ──
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next?.has(id)) next?.delete(id); else next?.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds?.size === users?.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(users?.map(u => u?.id)));
    }
  };

  const selectedUsers = users?.filter(u => selectedIds?.has(u?.id));

  // ─── file upload (Excel) ─────────────────────────────────────────────────
  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setImportError('');
    setParsedRows([]);
    setImportDone(false);
    setSyncGroups(null);
    const ext = file?.name?.split('.')?.pop()?.toLowerCase();
    if (ext !== 'xlsx' && ext !== 'xls') {
      setImportError('Molimo otpremite Excel fajl (.xlsx ili .xls)');
      return;
    }
    setImportFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const rows = parseExcelUsers(ev?.target?.result);
        if (rows?.length === 0) {
          setImportError('Nije pronađen validan sadržaj. Proverite da li fajl ima kolonu "Email".');
        } else {
          setParsedRows(rows);
        }
      } catch (err) {
        setImportError('Greška pri čitanju fajla: ' + err?.message);
      }
    };
    reader?.readAsArrayBuffer(file);
  };

  const handleSaveImport = async () => {
    if (parsedRows?.length === 0) return;
    setImportLoading(true);
    setImportError('');
    try {
      const rows = parsedRows?.map(r => ({
        email: r?.email,
        full_name: r?.fullName || null,
        role: r?.role || null,
        status: r?.status || null,
        state: r?.state || null,
        standing: r?.standing || null,
        maintenance_date: r?.maintenance || null,
        sync_status: 'not_attempted',
        notes: r?.details || null,
      }));
      const { error } = await supabase?.from('profile_sync_tracking')?.upsert(rows, { onConflict: 'email', ignoreDuplicates: false });
      if (error) throw error;
      setImportDone(true);
    } catch (err) {
      setImportError(err?.message || 'Greška pri čuvanju podataka');
    } finally {
      setImportLoading(false);
    }
  };

  const handleMatchSync = async () => {
    if (parsedRows?.length === 0) return;
    setSyncLoading(true);
    setSyncGroups(null);
    try {
      const { data: localUsers, error } = await supabase?.from('user_profiles')?.select('email, full_name, id');
      if (error) throw error;
      const importedEmailSet = new Set(parsedRows.map(r => r.email?.toLowerCase()));
      const localEmailSet = new Set((localUsers || []).map(u => u.email?.toLowerCase()));
      const matched = parsedRows?.filter(r => localEmailSet?.has(r?.email?.toLowerCase()));
      const onlyValuebook = parsedRows?.filter(r => !localEmailSet?.has(r?.email?.toLowerCase()));
      const onlyMarketplace = (localUsers || [])?.filter(u => !importedEmailSet?.has(u?.email?.toLowerCase()));
      const matchedEmails = matched?.map(r => r?.email);
      if (matchedEmails?.length > 0) {
        await supabase?.from('profile_sync_tracking')?.update({ sync_status: 'synced', valuebook_verified: true, last_sync_success: new Date()?.toISOString() })?.in('email', matchedEmails);
      }
      setSyncGroups({ matched, onlyValuebook, onlyMarketplace });
    } catch (err) {
      setImportError(err?.message || 'Greška pri sinhronizaciji');
    } finally {
      setSyncLoading(false);
    }
  };

  // ─── render ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      <div className="flex">
        <AdminSidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(p => !p)} />
        <main
          className={`flex-1 transition-all duration-300 pt-4 pb-12 px-6 ${
            sidebarCollapsed ? 'ml-20' : 'ml-64'
          }`}
        >
          {/* ── page header ── */}
          <div className="mb-8 mt-2">
            <h1 className="text-2xl font-bold text-slate-800">Upravljanje korisnicima</h1>
            <p className="text-slate-500 text-sm mt-1">
              Pregled registrovanih korisnika i uvoz podataka sa valuebook.app
            </p>
          </div>

          {/* ══════════════════════════════════════════════════════════════ */}
          {/* SECTION 1 – Registered users                                  */}
          {/* ══════════════════════════════════════════════════════════════ */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 mb-8">
            {/* header row */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-6 py-5 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-800">Registrovani korisnici</h2>
                  <p className="text-xs text-slate-400">{totalCount} ukupno</p>
                </div>
              </div>
              <button
                onClick={loadUsers}
                className="flex items-center gap-2 text-sm text-slate-500 hover:text-blue-600 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Osveži
              </button>
            </div>

            {/* filters */}
            <div className="flex flex-col sm:flex-row gap-3 px-6 py-4 border-b border-slate-50">
              <div className="flex gap-2 flex-wrap">
                {TIME_FILTERS?.map(tf => (
                  <button
                    key={tf?.value}
                    onClick={() => setTimeFilter(tf?.value)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      timeFilter === tf?.value
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {tf?.label}
                  </button>
                ))}
              </div>
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery === 'all' ? '' : searchQuery}
                  onChange={e => setSearchQuery(e?.target?.value || 'all')}
                  placeholder="Pretraži po email-u ili imenu…"
                  className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50"
                />
              </div>
            </div>

            {/* ── Bulk action bar ── */}
            {selectedIds?.size > 0 && (
              <div className="flex items-center gap-3 px-6 py-3 bg-blue-50 border-b border-blue-100">
                <span className="text-sm font-medium text-blue-700">
                  {selectedIds?.size} korisnik{selectedIds?.size === 1 ? '' : selectedIds?.size < 5 ? 'a' : 'a'} označeno
                </span>
                <button
                  onClick={() => setEmailModal({ users: selectedUsers })}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Mail className="w-3.5 h-3.5" />
                  Pošalji email svima
                </button>
                <button
                  onClick={() => setSelectedIds(new Set())}
                  className="ml-auto text-xs text-blue-500 hover:text-blue-700"
                >
                  Poništi selekciju
                </button>
              </div>
            )}

            {/* table */}
            <div className="overflow-x-auto">
              {usersLoading ? (
                <div className="flex items-center justify-center py-16 text-slate-400">
                  <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                  Učitavanje…
                </div>
              ) : usersError ? (
                <div className="flex items-center gap-2 text-red-600 px-6 py-8 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {usersError}
                </div>
              ) : users?.length === 0 ? (
                <div className="text-center py-16 text-slate-400 text-sm">
                  <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  Nema pronađenih korisnika
                </div>
              ) : (
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
                      <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedIds?.size === users?.length && users?.length > 0}
                          onChange={toggleSelectAll}
                          className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        />
                      </th>
                      <th className="px-4 py-3 text-left font-medium">Korisnik</th>
                      <th className="px-4 py-3 text-left font-medium">Email</th>
                      <th className="px-4 py-3 text-left font-medium">Uloga</th>
                      <th className="px-4 py-3 text-left font-medium">Status</th>
                      <th className="px-4 py-3 text-left font-medium">Valuebook link</th>
                      <th className="px-4 py-3 text-left font-medium">Registrovan</th>
                      <th className="px-4 py-3 text-left font-medium">Akcije</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {users?.map(u => (
                      <tr
                        key={u?.id}
                        className={`hover:bg-slate-50/60 transition-colors ${
                          selectedIds?.has(u?.id) ? 'bg-blue-50/40' : ''
                        }`}
                      >
                        {/* checkbox */}
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedIds?.has(u?.id)}
                            onChange={() => toggleSelect(u?.id)}
                            className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                          />
                        </td>

                        {/* user */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold text-xs flex-shrink-0">
                              {(u?.full_name || u?.email || '?')?.[0]?.toUpperCase()}
                            </div>
                            <span className="text-sm font-medium text-slate-800">{u?.full_name || '—'}</span>
                          </div>
                        </td>

                        {/* email */}
                        <td className="px-4 py-3 text-sm text-slate-600">{u?.email}</td>

                        {/* role */}
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${
                            ROLE_COLORS?.[u?.role] || 'bg-gray-100 text-gray-700'
                          }`}>
                            {ROLE_LABELS?.[u?.role] || u?.role || '—'}
                          </span>
                        </td>

                        {/* status */}
                        <td className="px-4 py-3">
                          {u?.is_banned ? (
                            <span className="inline-flex items-center gap-1 text-xs text-red-600">
                              <XCircle className="w-3.5 h-3.5" /> Banovan
                            </span>
                          ) : u?.is_suspended ? (
                            <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                              <Clock className="w-3.5 h-3.5" /> Suspendovan
                            </span>
                          ) : u?.is_verified ? (
                            <span className="inline-flex items-center gap-1 text-xs text-green-600">
                              <CheckCircle2 className="w-3.5 h-3.5" /> Verifikovan
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                              <Minus className="w-3.5 h-3.5" /> Aktivan
                            </span>
                          )}
                        </td>

                        {/* valuebook link */}
                        <td className="px-4 py-3">
                          {u?.valuebook_link ? (
                            <a
                              href={u?.valuebook_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                            >
                              <Link2 className="w-3 h-3" />
                              Link
                            </a>
                          ) : (
                            <span className="text-xs text-slate-300">—</span>
                          )}
                        </td>

                        {/* registered */}
                        <td className="px-4 py-3 text-xs text-slate-400">
                          {u?.created_at ? new Date(u.created_at)?.toLocaleDateString('sr-RS') : '—'}
                        </td>

                        {/* action buttons */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            {/* Send email */}
                            <button
                              onClick={() => setEmailModal({ users: [u] })}
                              title="Pošalji poruku"
                              className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-50 text-blue-700 text-xs font-medium rounded-lg hover:bg-blue-100 transition-colors whitespace-nowrap"
                            >
                              <Mail className="w-3.5 h-3.5" />
                              <span className="hidden xl:inline">Poruka</span>
                            </button>

                            {/* Suspend */}
                            {!u?.is_suspended && !u?.is_banned && (
                              <button
                                onClick={() => setSuspendModal(u)}
                                title="Blokiraj korisnika"
                                className="flex items-center gap-1 px-2.5 py-1.5 bg-amber-50 text-amber-700 text-xs font-medium rounded-lg hover:bg-amber-100 transition-colors whitespace-nowrap"
                              >
                                <Lock className="w-3.5 h-3.5" />
                                <span className="hidden xl:inline">Blokiraj</span>
                              </button>
                            )}

                            {/* Ban + archive */}
                            {!u?.is_banned && (
                              <button
                                onClick={() => setBanModal(u)}
                                title="Banovaj i arhiviraj"
                                className="flex items-center gap-1 px-2.5 py-1.5 bg-red-50 text-red-700 text-xs font-medium rounded-lg hover:bg-red-100 transition-colors whitespace-nowrap"
                              >
                                <Ban className="w-3.5 h-3.5" />
                                <span className="hidden xl:inline">Banovaj</span>
                              </button>
                            )}

                            {/* Reinstate (if suspended or banned) */}
                            {(u?.is_suspended || u?.is_banned) && (
                              <button
                                onClick={async () => {
                                  try {
                                    if (u?.is_banned) {
                                      await adminUserService?.unbanUser(u?.id);
                                    } else {
                                      await adminUserService?.unsuspendUser(u?.id);
                                    }
                                    loadUsers();
                                  } catch (err) {
                                    console.error('Reinstate error:', err);
                                  }
                                }}
                                title={u?.is_banned ? 'Ukloni ban i vrati aktivnosti' : 'Ukloni suspenziju'}
                                className="flex items-center gap-1 px-2.5 py-1.5 bg-green-50 text-green-700 text-xs font-medium rounded-lg hover:bg-green-100 transition-colors whitespace-nowrap"
                              >
                                <RotateCcw className="w-3.5 h-3.5" />
                                <span className="hidden xl:inline">{u?.is_banned ? 'Unban' : 'Unsuspend'}</span>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* ══════════════════════════════════════════════════════════════ */}
          {/* SECTION 2 – ValueBook.app import + sync                       */}
          {/* ══════════════════════════════════════════════════════════════ */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100">
            <div
              className="flex items-center justify-between px-6 py-5 cursor-pointer select-none"
              onClick={() => setShowImport(p => !p)}
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-violet-50 flex items-center justify-center">
                  <FileUp className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-800">Uvoz korisnika sa valuebook.app</h2>
                  <p className="text-xs text-slate-400">Otpremite Excel tabelu i uparite korisnike po email-u</p>
                </div>
              </div>
              <ChevronDown
                className={`w-5 h-5 text-slate-400 transition-transform duration-200 ${
                  showImport ? 'rotate-180' : ''
                }`}
              />
            </div>

            {showImport && (
              <div className="border-t border-slate-100 px-6 py-6 space-y-6">
                <div className="bg-slate-50 rounded-xl p-4 text-xs text-slate-500 space-y-1">
                  <p className="font-semibold text-slate-700 mb-1">Očekivani format Excel fajla (.xlsx):</p>
                  <p>Kolone: <code className="bg-white px-1 rounded">Full Name</code>, <code className="bg-white px-1 rounded">Email</code>, <code className="bg-white px-1 rounded">Role</code>, <code className="bg-white px-1 rounded">Status</code>, <code className="bg-white px-1 rounded">State</code>, <code className="bg-white px-1 rounded">Standing</code>, <code className="bg-white px-1 rounded">Maintenance</code>, <code className="bg-white px-1 rounded">Details</code></p>
                  <p className="text-slate-400 mt-1">Kolona <code className="bg-white px-1 rounded">Email</code> je obavezna. Ostale kolone su opcione.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Otpremite Excel fajl (.xlsx)</label>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 px-4 py-2.5 bg-violet-600 text-white text-sm font-medium rounded-xl cursor-pointer hover:bg-violet-700 transition-colors">
                      <Upload className="w-4 h-4" />
                      Izaberi fajl
                      <input type="file" accept=".xlsx,.xls" onChange={handleFileChange} className="hidden" />
                    </label>
                    {importFile && (
                      <span className="text-sm text-slate-600 flex items-center gap-1">
                        <Check className="w-4 h-4 text-green-500" />
                        {importFile?.name}
                      </span>
                    )}
                  </div>
                  {importError && (
                    <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {importError}
                    </p>
                  )}
                </div>

                {parsedRows?.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-medium text-slate-700">Pregled — {parsedRows?.length} korisnika</p>
                      <div className="flex gap-2">
                        <button
                          onClick={handleSaveImport}
                          disabled={importLoading || importDone}
                          className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white text-sm rounded-xl hover:bg-slate-700 disabled:opacity-50 transition-colors"
                        >
                          {importLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : importDone ? <CheckCircle2 className="w-4 h-4 text-green-400" /> : <FileUp className="w-4 h-4" />}
                          {importDone ? 'Sačuvano' : 'Sačuvaj u bazu'}
                        </button>
                        <button
                          onClick={handleMatchSync}
                          disabled={syncLoading}
                          className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white text-sm rounded-xl hover:bg-violet-700 disabled:opacity-50 transition-colors"
                        >
                          {syncLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                          Upari po email-u
                        </button>
                      </div>
                    </div>
                    <div className="overflow-x-auto rounded-xl border border-slate-100">
                      <table className="min-w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr className="text-xs text-slate-500 uppercase tracking-wide">
                            <th className="px-4 py-3 text-left font-medium">Full Name</th>
                            <th className="px-4 py-3 text-left font-medium">Email</th>
                            <th className="px-4 py-3 text-left font-medium">Role</th>
                            <th className="px-4 py-3 text-left font-medium">Status</th>
                            <th className="px-4 py-3 text-left font-medium">State</th>
                            <th className="px-4 py-3 text-left font-medium">Standing</th>
                            <th className="px-4 py-3 text-left font-medium">Maintenance</th>
                            <th className="px-4 py-3 text-left font-medium">Details</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                          {parsedRows?.map((row, idx) => (
                            <tr key={idx} className="hover:bg-slate-50/60 transition-colors">
                              <td className="px-4 py-2.5 text-slate-700 whitespace-nowrap">{row?.fullName || '—'}</td>
                              <td className="px-4 py-2.5 text-slate-600">{row?.email}</td>
                              <td className="px-4 py-2.5 text-slate-500 text-xs">{row?.role || '—'}</td>
                              <td className="px-4 py-2.5 text-slate-500 text-xs">{row?.status || '—'}</td>
                              <td className="px-4 py-2.5 text-slate-500 text-xs">{row?.state || '—'}</td>
                              <td className="px-4 py-2.5 text-slate-500 text-xs">{row?.standing || '—'}</td>
                              <td className="px-4 py-2.5 text-slate-500 text-xs whitespace-nowrap">{row?.maintenance || '—'}</td>
                              <td className="px-4 py-2.5 text-slate-500 text-xs">{row?.details || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {syncGroups && (
                  <div className="space-y-5">
                    <div className="flex flex-wrap gap-3">
                      <div className="flex items-center gap-2 bg-green-50 border border-green-100 rounded-xl px-4 py-3">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="text-xs text-green-600 font-medium">Upareni</p>
                          <p className="text-xl font-bold text-green-700">{syncGroups?.matched?.length}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
                        <AlertCircle className="w-5 h-5 text-amber-500" />
                        <div>
                          <p className="text-xs text-amber-600 font-medium">Samo na valuebook.app</p>
                          <p className="text-xl font-bold text-amber-600">{syncGroups?.onlyValuebook?.length}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
                        <Users className="w-5 h-5 text-blue-500" />
                        <div>
                          <p className="text-xs text-blue-600 font-medium">Samo na Marketplace</p>
                          <p className="text-xl font-bold text-blue-600">{syncGroups?.onlyMarketplace?.length}</p>
                        </div>
                      </div>
                    </div>

                    {syncGroups?.matched?.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-green-700 mb-2 flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4" />
                          Upareni — registrovani na obe platforme ({syncGroups?.matched?.length})
                        </h3>
                        <div className="overflow-x-auto rounded-xl border border-green-100">
                          <table className="min-w-full text-sm">
                            <thead className="bg-green-50">
                              <tr className="text-xs text-green-700 uppercase tracking-wide">
                                <th className="px-4 py-2 text-left font-medium">Full Name</th>
                                <th className="px-4 py-2 text-left font-medium">Email</th>
                                <th className="px-4 py-2 text-left font-medium">Role</th>
                                <th className="px-4 py-2 text-left font-medium">Status</th>
                                <th className="px-4 py-2 text-left font-medium">Standing</th>
                                <th className="px-4 py-2 text-left font-medium">Maintenance</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-green-50">
                              {syncGroups?.matched?.map((row, idx) => (
                                <tr key={idx} className="bg-green-50/30">
                                  <td className="px-4 py-2 text-slate-700">{row?.fullName || '—'}</td>
                                  <td className="px-4 py-2 text-slate-600">{row?.email}</td>
                                  <td className="px-4 py-2 text-xs text-slate-500">{row?.role || '—'}</td>
                                  <td className="px-4 py-2 text-xs text-slate-500">{row?.status || '—'}</td>
                                  <td className="px-4 py-2 text-xs text-slate-500">{row?.standing || '—'}</td>
                                  <td className="px-4 py-2 text-xs text-slate-500">{row?.maintenance || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {syncGroups?.onlyValuebook?.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-amber-700 mb-2 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          Samo na valuebook.app — nisu registrovani na Marketplace ({syncGroups?.onlyValuebook?.length})
                        </h3>
                        <div className="overflow-x-auto rounded-xl border border-amber-100">
                          <table className="min-w-full text-sm">
                            <thead className="bg-amber-50">
                              <tr className="text-xs text-amber-700 uppercase tracking-wide">
                                <th className="px-4 py-2 text-left font-medium">Full Name</th>
                                <th className="px-4 py-2 text-left font-medium">Email</th>
                                <th className="px-4 py-2 text-left font-medium">Role</th>
                                <th className="px-4 py-2 text-left font-medium">Status</th>
                                <th className="px-4 py-2 text-left font-medium">Standing</th>
                                <th className="px-4 py-2 text-left font-medium">Maintenance</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-amber-50">
                              {syncGroups?.onlyValuebook?.map((row, idx) => (
                                <tr key={idx} className="bg-amber-50/30">
                                  <td className="px-4 py-2 text-slate-700">{row?.fullName || '—'}</td>
                                  <td className="px-4 py-2 text-slate-600">{row?.email}</td>
                                  <td className="px-4 py-2 text-xs text-slate-500">{row?.role || '—'}</td>
                                  <td className="px-4 py-2 text-xs text-slate-500">{row?.status || '—'}</td>
                                  <td className="px-4 py-2 text-xs text-slate-500">{row?.standing || '—'}</td>
                                  <td className="px-4 py-2 text-xs text-slate-500">{row?.maintenance || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {syncGroups?.onlyMarketplace?.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-blue-700 mb-2 flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          Samo na Marketplace — nisu u valuebook.app uvozu ({syncGroups?.onlyMarketplace?.length})
                        </h3>
                        <div className="overflow-x-auto rounded-xl border border-blue-100">
                          <table className="min-w-full text-sm">
                            <thead className="bg-blue-50">
                              <tr className="text-xs text-blue-700 uppercase tracking-wide">
                                <th className="px-4 py-2 text-left font-medium">Full Name</th>
                                <th className="px-4 py-2 text-left font-medium">Email</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-blue-50">
                              {syncGroups?.onlyMarketplace?.map((u, idx) => (
                                <tr key={idx} className="bg-blue-50/30">
                                  <td className="px-4 py-2 text-slate-700">{u?.full_name || '—'}</td>
                                  <td className="px-4 py-2 text-slate-600">{u?.email}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* ── Modals ── */}
      {emailModal && (
        <SendEmailModal
          users={emailModal?.users}
          onClose={() => setEmailModal(null)}
          onSent={() => { setSelectedIds(new Set()); }}
        />
      )}
      {suspendModal && (
        <SuspendModal
          user={suspendModal}
          adminId={currentAdminId}
          onClose={() => setSuspendModal(null)}
          onDone={() => loadUsers()}
        />
      )}
      {banModal && (
        <BanArchiveModal
          user={banModal}
          adminId={currentAdminId}
          onClose={() => setBanModal(null)}
          onDone={() => loadUsers()}
        />
      )}
    </div>
  );
}